/**
 * 참여그룹 가입 — 그룹을 고르고 초대코드를 넣으면 **서버가** 확인한다(QBase joinChallengeGroup, 2026-10-07).
 *
 * 예전에는 전체 그룹 목록을 **초대코드째** 받아(participantGroups) 브라우저가 코드를 대조했다 — 누구나 모든 그룹의 코드를 볼 수
 * 있었고, 가입은 브라우저 저장소에만 남았다. 이제 고를 목록은 코드 없는 목록(challengeGroups)이고, 가입은 서버에 남아 그 그룹에
 * 공개된 챌린지가 서버에서 보인다.
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiUserPlus, FiAlertTriangle } from 'react-icons/fi';
import AppModal from './common/AppModal';
import { listChallengeGroups, joinChallengeGroup } from '../services/livesqlRepo';
import { formatApiError } from '../utils/formatters';
import './ParticipationGroupModal.css';
import memoWindow from './common/memoWindow';

const ParticipationGroupModal = ({ isOpen, onClose, onJoined, joinedIds }) => {
    const { t } = useTranslation();
    const [groups, setGroups] = useState(null);
    const [groupId, setGroupId] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    // 열 때 한 번만 비우고 읽는다 — 언어가 바뀌었다고(t) 쓰던 코드를 지우지 않게
    const tRef = useRef(t);
    tRef.current = t;
    useEffect(() => {
        if (!isOpen) return undefined;
        let alive = true;
        setGroupId(''); setInviteCode(''); setError(''); setGroups(null);
        listChallengeGroups()
            .then((g) => { if (alive) setGroups(g); })
            .catch((e) => { if (alive) { setGroups([]); setError(formatApiError(e, tRef.current)); } });
        return () => { alive = false; };
    }, [isOpen]);

    const submit = async (e) => {
        e.preventDefault();
        if (!groupId || !inviteCode.trim()) return;
        setBusy(true); setError('');
        try {
            const g = await joinChallengeGroup(groupId, inviteCode);
            onJoined({ qryChallengePtcpGrpId: g.qryChallengePtcpGrpId || groupId, ptcpGrpNm: g.ptcpGrpNm });
            onClose();
        } catch (err) {
            setError(formatApiError(err, t));
        } finally {
            setBusy(false);
        }
    };

    const options = (groups || []).filter(g => !(joinedIds || []).includes(g.id));
    return (
        <AppModal open={isOpen} onClose={onClose} title={t('challenge:grp.newGroup')} icon={<FiUserPlus size={14} />} className="app-modal-default pgm-modal">
            <form className="pgm-form" onSubmit={submit}>
                <div className="pgm-info">{t('challenge:grp.joinIntro')}</div>
                <label className="pgm-field">
                    <span>{t('challenge:grp.group')}</span>
                    <select value={groupId} onChange={(e) => { setGroupId(e.target.value); setError(''); }} disabled={busy || groups === null}>
                        <option value="">{groups === null ? t('challenge:grp.loading') : t('challenge:grp.pickGroup')}</option>
                        {options.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                </label>
                {groups && !options.length ? <div className="pgm-hint">{t('challenge:grp.noMoreGroups')}</div> : null}
                <label className="pgm-field">
                    <span>{t('challenge:grp.code')}</span>
                    <input
                        value={inviteCode} onChange={(e) => { setInviteCode(e.target.value); setError(''); }}
                        placeholder={t('challenge:grp.codePlaceholder')} disabled={busy} autoComplete="off"
                    />
                </label>
                <div className="pgm-hint">{t('challenge:grp.codeFromAdmin')}</div>
                {error ? <div className="pgm-error"><FiAlertTriangle size={13} /> {error}</div> : null}
                <div className="pgm-actions">
                    <button type="button" className="app-btn" onClick={onClose} disabled={busy}>{t('common:ui.cancel')}</button>
                    <button type="submit" className="app-btn app-btn-primary" disabled={busy || !groupId || !inviteCode.trim()}>
                        {busy ? t('challenge:grp.joining') : t('challenge:grp.join')}
                    </button>
                </div>
            </form>
        </AppModal>
    );
};

ParticipationGroupModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onJoined: PropTypes.func.isRequired,
    joinedIds: PropTypes.arrayOf(PropTypes.string),
};
ParticipationGroupModal.defaultProps = { isOpen: false, joinedIds: [] };

// 편집기에 글자를 칠 때마다 App 이 다시 그려져도 값이 같으면 다시 그리지 않는다(common/memoWindow, 2026-10-26 — 입력 지연)
export default memoWindow(ParticipationGroupModal);
